// frontend/src/components/ProfileCard.js
import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import { ensureWebTokenHydrated } from "../utils/auth-sync";
import { api } from "api";
import { isIOS } from "../utils/platform";
import CropperModal from "./CropperModal";
import ActivityLightbox from "./ActivityLightbox";
import ConnectionsModal from "./ConnectionsModal";
import CircleToast from "./CircleToast";
import ProfileFlowers from "./ProfileFlowers";
import { DEFAULT_CIRCLE_IMAGE, DEFAULT_PROFILE_IMAGE } from "../utils/blank-image";
import PillInput from "./PillInput";
import SpotifyPicker from "./SpotifyPicker";

import {
  Check,
  Headphones,
} from "phosphor-react";

// helper: attach user to an activity if missing
const withUser = (act, user) => {
  if (!act || act.user || !user) return act;
  const name =
    user.display_name ||
    user.name ||
    [user.first_name, user.last_name].filter(Boolean).join(" ") ||
    user.username ||
    (user.id ? `User #${user.id}` : "User");
  return {
    ...act,
    user: {
      id: user.id,
      name,
      profile_pic_url: user.profile_pic_url || user.avatar_url || null,
    },
  };
};

// canvas helper to crop -> Blob
async function getCroppedImage(dataUrl, cropAreaPixels, mime = "image/png", quality = 0.92) {
  const img = await new Promise((res, rej) => {
    const i = new Image();
    i.onload = () => res(i);
    i.onerror = rej;
    i.src = dataUrl;
  });
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.floor(cropAreaPixels.width));
  canvas.height = Math.max(1, Math.floor(cropAreaPixels.height));
  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(
    img,
    cropAreaPixels.x,
    cropAreaPixels.y,
    cropAreaPixels.width,
    cropAreaPixels.height,
    0,
    0,
    canvas.width,
    canvas.height
  );
  const blob = await new Promise((res) => canvas.toBlob(res, mime, quality));
  return blob;
}

const useIsMobile = () => {
  const [isMobile, setIsMobile] = React.useState(
    typeof window !== "undefined" ? window.innerWidth < 768 : false
  );
  React.useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return isMobile;
};

/**
 * Props:
 * - user, showLatestLink, canEdit, readOnly, onUserUpdate
 * - NEW (optional, non-breaking):
 *   disableConnectionsModal (bool)
 *   onFollowersClick (func)
 *   onFollowingClick (func)
 *   onActivitiesClick (func)
 *   latestActivity (object)  // prefer this if provided
 */
const ProfileCard = ({
  user,
  showLatestLink = true,
  canEdit = true,
  readOnly = false,
  onUserUpdate,
  isSelf,
  connStatus,
  onConnect,
  onAccept,
  onRemove,
  disableConnectionsModal = false,
  onFollowersClick,
  onFollowingClick,
  onActivitiesClick,
  latestActivity: latestFromProp,

}) => {
  const navigate = useNavigate();
  const [uploading, setUploading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [localUser, setLocalUser] = useState(user);
  const fileInputRef = useRef(null);

  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [editName, setEditName] = useState("");
  const [editUsername, setEditUsername] = useState("");
  const [editBio, setEditBio] = useState("");

  const [editExploring, setEditExploring] = useState([]);
  const [editHomePlaces, setEditHomePlaces] = useState([]);

  const [editOnRepeat, setEditOnRepeat] = useState({
    type: "none",
    title: "",
    artist: "",
    url: "",
    spotify_id: "",
  });

  const [editFlowersEnabled, setEditFlowersEnabled] =
    useState(true);

  const [toast, setToast] = useState(null);
  const toastTimerRef = useRef(null);

  // cropper state
  const [cropSrc, setCropSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [cropPixels, setCropPixels] = useState(null);
  const [showCropper, setShowCropper] = useState(false);

  // Activity lightbox state
  const [lb, setLb] = useState({ open: false, activity: null, id: null });

  // Visibility state for "can viewer see latest activity?"
  const [canSeeLatest, setCanSeeLatest] = useState(true);
  const [meId, setMeId] = useState(null);

  // desktop ConnectionsModal state (preserved)
  const [connOpen, setConnOpen] = useState(false);
  const [connTab, setConnTab] = useState("overview");

  // If parent controls user, use it directly; otherwise use local shadow
  const activeUser = onUserUpdate ? user : localUser;
  const isMobile = useIsMobile();
  const isCompact = isIOS || isMobile;

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) {
        clearTimeout(toastTimerRef.current);
      }
    };
  }, []);

  // keep ref to latest user
  const userRef = useRef(activeUser);
  useEffect(() => {
    userRef.current = onUserUpdate ? user : localUser;
  }, [onUserUpdate, user, localUser]);

  // mirror incoming prop for read-only pages
  useEffect(() => {
    if (!onUserUpdate) setLocalUser(user);
  }, [user, onUserUpdate]);

  // react to global follower bumps
  useEffect(() => {
    const handler = (e) => {
      const { followingDelta = 0, followersDelta = 0 } = e.detail || {};
      if (!followingDelta && !followersDelta) return;
      const apply = (u = {}) => ({
        ...u,
        following: Math.max(0, (u.following || 0) + followingDelta),
        followers: Math.max(0, (u.followers || 0) + followersDelta),
      });
      const current = userRef.current;
      const next = apply(current || {});
      if (onUserUpdate) onUserUpdate(next);
      else setLocalUser(next);
    };
    window.addEventListener("me:counts", handler);
    return () => window.removeEventListener("me:counts", handler);
  }, [onUserUpdate]);

  // Hook must be before any early return
  const onCropComplete = useCallback((_area, areaPixels) => {
    setCropPixels(areaPixels);
  }, []);

  // Helper: decide if an activity object is "rich" enough to show details without a refetch
  const isRichActivity = (a) => {
    if (!a) return false;
    if (a.mood || a.duration || a.location) return true;
    if (Array.isArray(a.tags) && a.tags.length) return true;
    if (a.description) return true;
    if (a.media_url || a.mediaUrl || a.image_url || a.video_url) return true;
    return false;
  };

  useEffect(() => {
    if (!lb.open || !lb.id) return;
  
    const isRichActivity = (a) => {
      if (!a) return false;
      if (a.mood || a.duration || a.location) return true;
      if (Array.isArray(a.tags) && a.tags.length) return true;
      if (a.description) return true;
      if (a.media_url || a.mediaUrl || a.image_url || a.video_url) return true;
      return false;
    };
  
    const hasMatchingRich =
      lb.activity &&
      Number(lb.activity?.id) === Number(lb.id) &&
      isRichActivity(lb.activity);
    if (hasMatchingRich) return;
  
    let cancelled = false;
    (async () => {
      try {
        // ensure token is mirrored to localStorage/sessionStorage
        await ensureWebTokenHydrated();
  
        const token =
          localStorage.getItem("token") ||
          sessionStorage.getItem("token") ||
          null;
        const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
  
        const tryGet = async (path) => {
          try { const r = await api.get(path, { headers }); return r?.data || null; }
          catch { return null; }
        };
  
        const data =
          (await tryGet(`/activities/${lb.id}`)) ||
          (await tryGet(`/activity/${lb.id}`)) ||
          (await tryGet(`/api/activities/${lb.id}`)) ||
          (await tryGet(`/api/activity/${lb.id}`)) || // <— extra common variant
          null;
  
        if (!cancelled && data?.id) {
          setLb(prev => ({ ...prev, activity: withUser(data, activeUser) }));
        }
      } catch (e) {
        console.error("Failed to fetch activity:", e);
      }
    })();
  
    return () => { cancelled = true; };
  }, [lb.open, lb.id, activeUser]);
  



  // ---------- Determine visibility: can the viewer see this user's latest activity? ----------
  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      try {
        // No user? default allow
        if (!activeUser?.id) {
          setCanSeeLatest(true);
          return;
        }

        // Make sure localStorage/sessionStorage has the token (iOS/native)
        await ensureWebTokenHydrated();

        const token =
          localStorage.getItem("token") || sessionStorage.getItem("token") || null;
        const headers = token ? { Authorization: `Bearer ${token}` } : undefined;

        // Identify me
        let me = null;
        try {
          me = headers ? (await api.get("/users/me", { headers })).data : null;
        } catch {
          me = null;
        }
        if (!cancelled && me?.id) setMeId(me.id);

        // Self can always see
        if (me?.id && me.id === activeUser.id) {
          if (!cancelled) setCanSeeLatest(true);
          return;
        }

        // QUICK HINTS present on the user object itself (cover different backends)
        const hint =
          activeUser?.i_follow_them ??
          activeUser?.i_follow ??
          activeUser?.viewer_follows ??
          activeUser?.am_following ??
          activeUser?.viewer_is_following ??
          null;

        if (hint === true) {
          if (!cancelled) setCanSeeLatest(true);
          return;
        }

        // STATUS endpoint – accept multiple shapes/field names
        let status = null;
        try {
          status = headers
            ? (await api.get(`/connections/status/${activeUser.id}`, { headers })).data
            : null;
        } catch {
          status = null;
        }

        const followedViaStatus =
          status?.i_follow_them === true ||
          status?.am_following === true ||
          status?.viewer_follows === true ||
          status?.following === true ||
          status?.relation === "following" ||
          status?.relation === "mutual" ||
          status?.status === "accepted"; // some APIs use "accepted" for mutuals

        if (followedViaStatus) {
          if (!cancelled) setCanSeeLatest(true);
          return;
        }

        // FINAL FALLBACK: fetch my following list and check membership
        if (headers && me?.id) {
          try {
            const res = await api.get(`/connections/following/${me.id}`, { headers });
            const list = Array.isArray(res.data) ? res.data : [];
            const isFollowing = list.some(
              (u) => Number(u?.id) === Number(activeUser.id)
            );
            if (!cancelled) setCanSeeLatest(!!isFollowing);
            return;
          } catch {
            // ignore and fall through
          }
        }

        // Default: not allowed
        if (!cancelled) setCanSeeLatest(false);
      } catch {
        if (!cancelled) setCanSeeLatest(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [activeUser?.id]);


  // ----- helpers/labels -----
  const activitiesCount =
    typeof activeUser?.activitiesCount === "number"
      ? activeUser.activitiesCount
      : Array.isArray(activeUser?.activities)
      ? activeUser.activities.length
      : 0;

  const pickLabel = useCallback((a) =>
    (a?.title && String(a.title).trim()) ||
    (a?.name && String(a.name).trim()) ||
    (a?.description && String(a.description).trim()) ||
    (Array.isArray(a?.tags) && a.tags.length ? a.tags.join(", ") : "") ||
    "View activity"
  , []);

  // compute latest activity from what we already have
  const computeLatestFromLocal = useCallback((u) => {
    if (!u) return null;
    if (u.latest_activity && (u.latest_activity.id || u.latest_activity.activity_id)) {
      return {
        id: u.latest_activity.id ?? u.latest_activity.activity_id,
        title: pickLabel(u.latest_activity),
        created_at: u.latest_activity.created_at,
        timestamp: u.latest_activity.timestamp,
      };
    }
    if (!u.latest_activity && (u.latest_activity_id || u.last_activity_id)) {
      return {
        id: u.latest_activity_id ?? u.last_activity_id,
        title:
          u.latest_activity_title ??
          u.last_activity_title ??
          u.latest_activity_name ??
          "View activity",
        created_at:
          u.latest_activity_created_at ?? u.last_activity_created_at,
        timestamp: u.latest_activity_timestamp,
      };
    }
    if (Array.isArray(u.activities) && u.activities.length) {
      const arr = [...u.activities];
      const sorted = arr.sort(
        (a, b) =>
          new Date(b.timestamp || b.created_at) - new Date(a.timestamp || a.created_at)
      );
      const candidate = sorted[0];
      if (candidate?.id) {
        return {
          id: candidate.id,
          title: pickLabel(candidate),
          created_at: candidate.created_at,
          timestamp: candidate.timestamp,
        };
      }
    }
    return null;
  }, [pickLabel]);

  const [latestOverride, setLatestOverride] = useState(null);

  const latestActivity = useMemo(() => {
    // NEW: prefer latestFromProp if provided; else use existing logic unchanged
    return latestFromProp || latestOverride || computeLatestFromLocal(activeUser);
  }, [latestFromProp, latestOverride, activeUser, computeLatestFromLocal]);

  // If we are allowed to see latest but don't have it locally, try to fetch it explicitly.
  useEffect(() => {
    let cancelled = false;

    const fetchLatest = async () => {
      if (!showLatestLink) return;
      if (!canSeeLatest) return;
      if (latestActivity?.id) return; // we already have something

      try {
        const token = localStorage.getItem("token");
        const headers = token ? { Authorization: `Bearer ${token}` } : undefined;

        const tryGet = async (path) => {
          try {
            const r = await api.get(path, { headers });
            return r?.data || null;
          } catch {
            return null;
          }
        };

        const data =
          (await tryGet(`/users/${activeUser.id}/latest-activity`)) ||
          (await tryGet(`/activities/latest?user_id=${activeUser.id}`)) ||
          (await tryGet(`/api/users/${activeUser.id}/latest-activity`)) ||
          null;

        if (!cancelled && data?.id) {
          setLatestOverride({
            id: data.id,
            title: pickLabel(data),
            created_at: data.created_at,
            timestamp: data.timestamp,
          });
        }
      } catch {
        // ignore – we'll simply show the gated/fallback message
      }
    };

    if (activeUser?.id) fetchLatest();
    return () => {
      cancelled = true;
    };
  }, [activeUser?.id, canSeeLatest, latestActivity?.id, pickLabel, showLatestLink]);

  // early return AFTER all hooks
  if (!activeUser) return null;

  const goToMyActivities = () => {
    const url = new URL(window.location.href);
    url.searchParams.set("scope", "mine");
    window.history.pushState({}, "", url);
    window.dispatchEvent(new CustomEvent("feed:set-scope", { detail: { scope: "mine" } }));
    const main = document.querySelector("main");
    if (main) main.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Open ConnectionsModal (desktop) or navigate (mobile)
  const openList = async (kind) => {
    if (readOnly) return;

    // NEW: if page wants to own the modal, honor overrides or disable internal modal
    if (disableConnectionsModal) {
      if (kind === "followers" && typeof onFollowersClick === "function") {
        onFollowersClick();
        return;
      }
      if (kind === "following" && typeof onFollowingClick === "function") {
        onFollowingClick();
        return;
      }
      // Fallback: emit a page-level event with the desired tab
      window.dispatchEvent(
        new CustomEvent("connections:open", {
          detail: { tab: kind === "followers" ? "followers" : "following" },
        })
      );
      return;
    }

    if (isCompact) {
      await ensureWebTokenHydrated();
      navigate(`/connections?tab=${kind}`);
      return;
    }

    await ensureWebTokenHydrated();
    setConnTab(kind === "followers" ? "followers" : "following");
    setConnOpen(true);
    window.dispatchEvent(new CustomEvent("connections:open"));
  };

  const onAvatarClick = () => {
    if (!canEdit || readOnly || uploading) return;
    fileInputRef.current?.click();
  };

  // file choose -> open cropper
  const onFileSelected = async (e) => {
    if (readOnly) return;
    setErrorMsg("");
    const file = e.target.files?.[0];
    if (!file) return;

    const validTypes = ["image/jpeg", "image/png", "image/webp", "image/gif"];
    if (!validTypes.includes(file.type)) {
      setErrorMsg("Please choose a JPG, PNG, WEBP, or GIF image.");
      e.target.value = "";
      return;
    }
    const maxSizeBytes = 5 * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      setErrorMsg("File is too large. Max size is 5MB.");
      e.target.value = "";
      return;
    }

    try {
      const objectUrl = URL.createObjectURL(file);
      setCropSrc(objectUrl);
      setZoom(1);
      setCrop({ x: 0, y: 0 });
      setShowCropper(true);
    } catch {
      setErrorMsg("Could not open image.");
    } finally {
      e.target.value = "";
    }
  };

  const uploadCropped = async () => {
    if (!cropSrc || !cropPixels) return;
    setUploading(true);
    setErrorMsg("");
    try {
      const blob = await getCroppedImage(cropSrc, cropPixels, "image/png", 0.92);
      const file = new File([blob], "avatar.png", { type: "image/png" });
      const form = new FormData();
      form.append("file", file);

      const token = localStorage.getItem("token");
      const { data } = await api.post("/users/me/profile-picture", form, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      const newUrl = data?.profile_pic_url || data?.url || activeUser.profile_pic_url;
      const bustedUrl = newUrl
        ? `${newUrl}${newUrl.includes("?") ? "&" : "?"}t=${Date.now()}`
        : newUrl;

      const updatedUser = { ...activeUser, profile_pic_url: bustedUrl };
      if (onUserUpdate) onUserUpdate(updatedUser);
      else setLocalUser(updatedUser);

      setShowCropper(false);
      URL.revokeObjectURL(cropSrc);
      setCropSrc(null);
    } catch (err) {
      console.error("Avatar upload failed:", err);
      const msg =
        err?.response?.data?.detail ||
        err?.message ||
        "Upload failed. Please try again.";
      setErrorMsg(String(msg));
    } finally {
      setUploading(false);
    }
  };

  const cancelCrop = () => {
    if (cropSrc) URL.revokeObjectURL(cropSrc);
    setCropSrc(null);
    setShowCropper(false);
  };

  // Activity light box
  const handleOpenLatest = async () => {
    await ensureWebTokenHydrated();
  
    const idNum = Number(latestActivity?.id);
    if (!idNum || Number.isNaN(idNum)) return;
  
    // Prefer the full object coming from the parent (Profile.js)
    // so the lightbox has data immediately and doesn't get stuck on "Loading..."
    let activityObj = null;
  
    // If parent provided a full latest activity via prop, use it
    if (latestFromProp && Number(latestFromProp?.id) === idNum) {
      activityObj = withUser(latestFromProp, activeUser);
    } else if (Array.isArray(activeUser.activities)) {
      // Fallback: try to find it in this user's local activities
      const found = activeUser.activities.find(a => Number(a?.id) === idNum);
      if (found) activityObj = withUser(found, activeUser);
    }
  
    setLb({ open: true, activity: activityObj, id: idNum });
  };

  const showLatestAllowed = showLatestLink && canSeeLatest;

  const displayName =
    activeUser.display_name ||
    activeUser.name ||
    `User #${activeUser.id}`;

  const displayUsername =
    activeUser.username ||
    activeUser.email?.split("@")?.[0] ||
    `user${activeUser.id}`;

  const displayBio = activeUser.bio?.trim() || "";

  const hasProfileExtras =
    (Array.isArray(activeUser.currently_exploring) &&
      activeUser.currently_exploring.length > 0) ||
    Boolean(activeUser.on_repeat_url) ||
    (Array.isArray(activeUser.home_places) &&
      activeUser.home_places.length > 0);

  const showToast = (
    message,
    kind = "success",
    duration = 3000,
    copyAgain = false
  ) => {
    if (toastTimerRef.current) {
      clearTimeout(toastTimerRef.current);
    }

    setToast({
      message,
      kind,
      copyAgain,
    });

    // toastTimerRef.current = setTimeout(() => {
      // setToast(null);
    // }, duration);
  };

  const copyText = async (text) => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
      }

      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();

      const copied = document.execCommand("copy");
      document.body.removeChild(textarea);

      return copied;
    } catch {
      return false;
    }
  };

  const getProfileUrl = () => {
    return `${window.location.origin}/profile/${activeUser.id}`;
  };

  const shareProfile = async () => {
    const profileUrl = getProfileUrl();
    const ok = await copyText(profileUrl);

    if (ok) {
      showToast(
        "Profile link copied",
        "success",
        4400,
        true
      );
    } else {
      showToast(
        "Copy failed — please copy from the address bar",
        "error"
      );
    }
  };

  const startEditingProfile = () => {
    setEditName(displayName);
    setEditUsername(displayUsername);
    setEditBio(displayBio);

    setEditExploring(
      Array.isArray(activeUser.currently_exploring)
        ? activeUser.currently_exploring
        : []
    );

    setEditHomePlaces(
      Array.isArray(activeUser.home_places)
        ? activeUser.home_places
        : []
    );

    setEditOnRepeat({
      type:
        activeUser.on_repeat_type || "none",

      title:
        activeUser.on_repeat_title || "",

      artist:
        activeUser.on_repeat_artist || "",

      url:
        activeUser.on_repeat_url || "",

      spotify_id:
        activeUser.on_repeat_spotify_id || "",
    });

    setEditFlowersEnabled(
      activeUser.flowers_enabled !== false
    );

    setErrorMsg("");
    setIsEditingProfile(true);
  };

  const cancelEditingProfile = () => {
    setEditName(displayName);
    setEditUsername(displayUsername);
    setEditBio(displayBio);

    setEditExploring(
      Array.isArray(activeUser.currently_exploring)
        ? activeUser.currently_exploring
        : []
    );

    setEditHomePlaces(
      Array.isArray(activeUser.home_places)
        ? activeUser.home_places
        : []
    );

    setEditOnRepeat({
      type:
        activeUser.on_repeat_type || "none",
      title:
        activeUser.on_repeat_title || "",
      artist:
        activeUser.on_repeat_artist || "",
      url:
        activeUser.on_repeat_url || "",
      spotify_id:
        activeUser.on_repeat_spotify_id || "",
    });

    setEditFlowersEnabled(
      activeUser.flowers_enabled !== false
    );

    setErrorMsg("");
    setIsEditingProfile(false);
  };

  const saveProfileChanges = async () => {
    const nameClean = editName.trim();
    const usernameClean = editUsername
      .trim()
      .toLowerCase()
      .replace(/^@/, "");
    const bioClean = editBio.trim();

    if (!nameClean) {
      setErrorMsg("Display name cannot be empty.");
      return;
    }

    if (!/^[a-z0-9._]{3,30}$/.test(usernameClean)) {
      setErrorMsg(
        "Username must be 3–30 characters using lowercase letters, numbers, periods, or underscores."
      );
      return;
    }

    if (bioClean.length > 240) {
      setErrorMsg("Bio cannot exceed 240 characters.");
      return;
    }

    setSavingProfile(true);
    setErrorMsg("");

    try {
      await ensureWebTokenHydrated();

      const token =
        localStorage.getItem("token") ||
        sessionStorage.getItem("token");

      console.log(
        "SAVING editOnRepeat:",
        editOnRepeat
      );

      let repeatToSave = editOnRepeat;

      if (
        editOnRepeat.type === "playlist" &&
        editOnRepeat.url?.trim() &&
        !editOnRepeat.title?.trim()
      ) {
        try {
          const playlistResponse = await api.get(
            "/music/spotify/playlist",
            {
              params: {
                url: editOnRepeat.url.trim(),
              },
              headers: token
                ? {
                    Authorization: `Bearer ${token}`,
                  }
                : undefined,
            }
          );

          const playlist =
            playlistResponse.data?.playlist;

          if (playlist?.title) {
            repeatToSave = {
              type: "playlist",
              title: playlist.title || "",
              artist: playlist.owner || "",
              url:
                playlist.url ||
                editOnRepeat.url,
              spotify_id:
                playlist.spotify_id || "",
            };

            setEditOnRepeat(repeatToSave);
          }
        } catch (error) {
          console.error(
            "Playlist lookup before save failed:",
            error
          );
        }
      }

      console.log(
        "ACTUALLY SAVING:",
        repeatToSave
      );

      const { data } = await api.patch(
        "/users/me",
        {
          name: nameClean,
          username: usernameClean,
          bio: bioClean,

          currently_exploring: editExploring,
          home_places: editHomePlaces,

          on_repeat_type:
            repeatToSave.type || "none",

          on_repeat_title:
            repeatToSave.title || "",

          on_repeat_artist:
            repeatToSave.artist || "",

          on_repeat_url:
            repeatToSave.url || "",

          on_repeat_spotify_id:
            repeatToSave.spotify_id || "",

          flowers_enabled:
            editFlowersEnabled,
        },
        {
          headers: token
            ? { Authorization: `Bearer ${token}` }
            : undefined,
        }
      );

      console.log(
        "PATCH /users/me RESPONSE:",
        data
      );

      const updatedUser = {
        ...activeUser,
        ...data,

        name:
          data?.name ?? nameClean,

        username:
          data?.username ?? usernameClean,

        bio:
          data?.bio ?? bioClean,

        currently_exploring:
          data?.currently_exploring ??
          editExploring,

        home_places:
          data?.home_places ??
          editHomePlaces,

        on_repeat_type:
          data?.on_repeat_type ??
          repeatToSave.type ??
          "none",

        on_repeat_title:
          data?.on_repeat_title ??
          repeatToSave.title ??
          "",

        on_repeat_artist:
          data?.on_repeat_artist ??
          repeatToSave.artist ??
          "",

        on_repeat_url:
          data?.on_repeat_url ??
          repeatToSave.url ??
          "",

        on_repeat_spotify_id:
          data?.on_repeat_spotify_id ??
          repeatToSave.spotify_id ??
          "",

        flowers_enabled:
          data?.flowers_enabled ??
          editFlowersEnabled,
      };

      if (onUserUpdate) {
        onUserUpdate(updatedUser);
      } else {
        setLocalUser(updatedUser);
      }

      setIsEditingProfile(false);
      showToast("Profile updated", "success", 2500);
    } catch (err) {
      setErrorMsg(
        err?.response?.data?.detail ||
          "Could not update your profile"
      );
    } finally {
      setSavingProfile(false);
    }
  };

  return (
    <div className="rounded-2xl bg-white px-5 py-5">
      {canEdit && !readOnly && (
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          className="hidden"
          onChange={onFileSelected}
        />
      )}

      <div className="flex flex-col items-center text-center">
        {/* Profile photo */}
        <div className="group relative mt-1">
          <button
            type="button"
            onClick={onAvatarClick}
            disabled={!canEdit || readOnly || uploading}
            className={`block rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              canEdit && !readOnly
                ? "cursor-pointer"
                : "cursor-default"
            }`}
            title={
              canEdit && !readOnly
                ? uploading
                  ? "Uploading..."
                  : "Change profile photo"
                : undefined
            }
          >
            <img
              src={
                activeUser.profile_pic_url ||
                DEFAULT_PROFILE_IMAGE
              }
              className="h-20 w-20 rounded-full object-cover"
              alt={`${displayName} avatar`}
            />
          </button>

          {canEdit && !readOnly && !uploading && (
            <div
              className="
                pointer-events-none absolute inset-0
                flex items-center justify-center
                rounded-full bg-black/60
                text-xs font-medium text-white
                opacity-0 transition-opacity
                group-hover:opacity-100
              "
            >
              Edit
            </div>
          )}

          {uploading && (
            <div
              className="
                absolute inset-0
                flex items-center justify-center
                rounded-full bg-black/40
                text-xs text-white
              "
            >
              Uploading…
            </div>
          )}
        </div>

        {errorMsg && (
          <p className="mt-2 text-xs text-red-600">
            {errorMsg}
          </p>
        )}

        {isSelf && isEditingProfile ? (
          <div className="mt-3 flex w-full flex-col gap-2 text-left">
            <div>
              <label
                htmlFor="profile-display-name"
                className="mb-1 ml-3 block text-xs font-semibold text-gray-600"
              >
                Display Name
              </label>

              <input
                id="profile-display-name"
                type="text"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                maxLength={80}
                className="
                  w-full rounded-2xl !min-h-0 !min-w-0 border border-gray-200 
                  !min-h-0 !min-w-0 px-3 py-1 text-sm text-gray-900
                  outline-none focus:border-2 focus:border-blue-500
                "
              />
            </div>

            <div>
              <label
                htmlFor="profile-username"
                className="mb-1 ml-3 block text-xs font-semibold text-gray-600"
              >
                Username
              </label>

              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-400">
                  @
                </span>

                <input
                  id="profile-username"
                  type="text"
                  value={editUsername}
                  onChange={(e) =>
                    setEditUsername(
                      e.target.value
                        .toLowerCase()
                        .replace(/^@/, "")
                    )
                  }
                  maxLength={30}
                  className="
                    w-full rounded-2xl !min-h-0 !min-w-0 border border-gray-200
                    !min-w-0 !min-h-0 py-1 pl-6 pr-3 text-sm text-gray-900
                    outline-none focus:border-blue-500 focus:border-2"
                />
              </div>
            </div>

            <div>
              <div className="mb-1 ml-3 flex items-center justify-between">
                <label
                  htmlFor="profile-bio"
                  className="text-xs font-semibold text-gray-600"
                >
                  Bio
                </label>

                <span className="text-[11px] pr-3 text-gray-400">
                  {editBio.length}/240
                </span>
              </div>

              <textarea
                id="profile-bio"
                value={editBio}
                onChange={(e) => setEditBio(e.target.value)}
                maxLength={240}
                rows={3}
                className="
                  w-full resize-none rounded-2xl border border-gray-200
                  px-3 py-2 text-sm text-gray-900 leading-tight
                  outline-none focus:border-2 focus:border-blue-500
                "
              />
            </div>

            <div>
              <div className="mb-1 ml-3 flex items-center justify-between">
                <label className="text-xs font-semibold text-gray-600">
                  Lately into
                </label>

                <span className="pr-3 text-[11px] text-gray-400">
                  {editExploring.length}/5
                </span>
              </div>

              <PillInput
                value={editExploring}
                onChange={setEditExploring}
                maxCount={5}
                searchable
                placeholder="Add an interest"
                anotherPlaceholder="Add"
              />
            </div>

            <div>
              <label className="mb-1 ml-3 block text-xs font-semibold text-gray-600">
                Soundtrack
              </label>

              <SpotifyPicker
                value={editOnRepeat}
                onChange={setEditOnRepeat}
              />
            </div>

            <div>
              <div className="mb-1 ml-3 flex items-center justify-between">
                <label className="text-xs font-semibold text-gray-600">
                  Places that feel like home
                </label>

                <span className="pr-3 text-[11px] text-gray-400">
                  {editHomePlaces.length}/3
                </span>
              </div>

              <PillInput
                value={editHomePlaces}
                onChange={setEditHomePlaces}
                maxCount={3}
                searchable={false}
                placeholder="Add a place"
                anotherPlaceholder="Add"
              />
            </div>

            <label
              className="
                flex cursor-pointer items-center justify-between
                px-3 py-2
              "
            >
              <div>
                <p className="text-xs font-semibold text-gray-700">
                  Shoutouts
                </p>

                <p className="text-[11px] leading-tight text-gray-500">
                  Let people leave shoutouts on
                  <br className="hidden md:block" />
                  <span className="md:hidden"> </span>
                  your profile
                </p>
              </div>

              <input
                type="checkbox"
                checked={editFlowersEnabled}
                onChange={(event) =>
                  setEditFlowersEnabled(event.target.checked)
                }
                className="peer sr-only"
              />

              <div
                className={`
                  flex h-4 w-4 shrink-0
                  items-center justify-center
                  rounded-full border
                  transition-colors
                  ${
                    editFlowersEnabled
                      ? "border-blue-500 bg-blue-500"
                      : "border-slate-200 bg-blue-50"
                  }
                `}
              >
                <Check
                  size={12}
                  weight="bold"
                  className={`
                    text-white transition-opacity
                    ${
                      editFlowersEnabled
                        ? "opacity-100"
                        : "opacity-0"
                    }
                  `}
                />
              </div>
            </label>

            {errorMsg && (
              <p className="text-xs text-red-600">
                {errorMsg}
              </p>
            )}

            <div className="flex w-full justify-center gap-2">
              <button
                type="button"
                onClick={saveProfileChanges}
                disabled={savingProfile}
                className="
                  w-[34%] flex-1 rounded-full bg-gray-100
                  px-1 py-1.5 text-xs font-semibold text-gray-700
                  !min-h-0 !min-w-0
                  hover:bg-blue-200
                "
              >
                {savingProfile ? "Saving…" : "Save"}
              </button>

              <button
                type="button"
                onClick={cancelEditingProfile}
                disabled={savingProfile}
                className="
                  w-[34%] flex-1 rounded-full bg-gray-100
                  px-1 py-1.5 text-xs font-semibold text-gray-700
                  !min-h-0 !min-w-0
                  hover:bg-blue-200
                "
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Name */}
            {readOnly ? (
              <h2 className="mt-3 text-lg font-black tracking-[-0.03em] text-gray-900">
                {displayName}
              </h2>
            ) : (
              <Link
                to={`/profile/${activeUser.id}`}
                className="mt-3"
              >
                <h2 className="text-lg font-black tracking-[-0.03em] text-gray-900 hover:text-blue-600">
                  {displayName}
                </h2>
              </Link>
            )}

            {/* Username */}
            <p className="-mt-0.5 text-sm text-gray-500">
              @{displayUsername}
            </p>

            {/* Bio: render nothing when missing */}
            {displayBio && (
              <p className="mt-2 max-w-sm whitespace-pre-line text-sm leading-tight text-gray-600">
                {displayBio}
              </p>
            )}

            {hasProfileExtras ? (
              <div className="mt-3.5 space-y-2.5 text-center text-xs">
                {Array.isArray(activeUser.currently_exploring) &&
                  activeUser.currently_exploring.length > 0 && (
                    <div className="w-full">
                      <div className="mb-1 text-center">
                        <div className="inline-flex items-center gap-1.5 font-semibold text-blue-600">
                          <span>lately</span>
                        </div>
                      </div>

                      {activeUser.currently_exploring.length === 5 ? (
                        <div className="mx-auto flex max-w-[280px] flex-col items-center gap-1.5">
                          <div className="flex justify-center gap-1.5">
                            {activeUser.currently_exploring
                              .slice(0, 3)
                              .map((item) => (
                                <button
                                  key={item}
                                  type="button"
                                  onClick={() =>
                                    navigate(
                                      `/tags/${encodeURIComponent(item)}`
                                    )
                                  }
                                  className="
                                    rounded-full bg-blue-600
                                    !min-h-0 !min-w-0 px-2 py-0.5 text-xs
                                    font-medium text-white
                                    hover:bg-blue-200
                                  "
                                >
                                  {item}
                                </button>
                              ))}
                          </div>

                          <div className="flex justify-center gap-1.5">
                            {activeUser.currently_exploring
                              .slice(3)
                              .map((item) => (
                                <button
                                  key={item}
                                  type="button"
                                  onClick={() =>
                                    navigate(
                                      `/tags/${encodeURIComponent(item)}`
                                    )
                                  }
                                  className="
                                    rounded-full bg-blue-600
                                    !min-h-0 !min-w-0 px-2 py-0.5 text-xs
                                    font-medium text-white
                                    hover:bg-blue-200
                                  "
                                >
                                  {item}
                                </button>
                              ))}
                          </div>
                        </div>
                      ) : (
                        <div className="mx-auto flex max-w-[280px] flex-wrap justify-center gap-1.5">
                          {activeUser.currently_exploring.map(
                            (item) => (
                              <button
                                key={item}
                                type="button"
                                onClick={() =>
                                  navigate(
                                    `/tags/${encodeURIComponent(item)}`
                                  )
                                }
                                className="
                                  rounded-full bg-blue-600
                                  !min-h-0 !min-w-0 px-2 py-0.5 text-xs
                                  font-medium text-white
                                  hover:bg-blue-200
                                "
                              >
                                {item}
                              </button>
                            )
                          )}
                        </div>
                      )}
                    </div>
                  )}

                {Array.isArray(activeUser.home_places) &&
                  activeUser.home_places.length > 0 && (
                    <div>
                      <span className="font-semibold text-blue-600">
                        home
                      </span>

                      <div className="flex flex-wrap items-center justify-center gap-x-1 font-medium text-gray-800">
                        {activeUser.home_places.map(
                          (place, index) => (
                            <React.Fragment key={place}>
                              {index > 0 && (
                                <span className="text-blue-500">
                                  ·
                                </span>
                              )}
                              <span>{place}</span>
                            </React.Fragment>
                          )
                        )}
                      </div>
                    </div>
                  )}

                {activeUser.on_repeat_url && (
                  <div
                    className={`w-full min-w-0 ${
                      Array.isArray(
                        activeUser.currently_exploring
                      ) &&
                      activeUser.currently_exploring.length > 0 &&
                      !(
                        Array.isArray(activeUser.home_places) &&
                        activeUser.home_places.length > 0
                      )
                        ? "!mt-3.5 !mb-1"
                        : "!mt-2.5 !mb-1"
                    }`}
                  >
                    <a
                      href={activeUser.on_repeat_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`
                        mx-auto flex w-fit !min-w-0 !min-h-0
                        flex-col transition-colors
                        ${
                          activeUser.on_repeat_type === "track"
                            ? `
                              max-w-[280px] items-center text-center
                              rounded-3xl px-4 py-1.5 bg-blue-100 hover:bg-blue-200
                            `
                            : `
                              max-w-[240px] items-center text-center
                              rounded-3xl px-4 py-1.5 bg-blue-100 hover:bg-blue-200
                            `
                        }
                      `}
                    >
                      {activeUser.on_repeat_type === "track" ? (
                        <>
                          <div className="mb-0.5 flex items-center justify-center text-blue-700">
                            <Headphones
                              size={12}
                              weight="bold"
                            />
                          </div>

                          <div className="min-w-0">
                            <p className="whitespace-normal break-words text-xs font-semibold leading-tight text-gray-900 hover:text-blue-800">
                              {activeUser.on_repeat_title}
                            </p>

                            {activeUser.on_repeat_artist && (
                              <p className="whitespace-normal break-words text-[10px] leading-tight text-gray-500">
                                {activeUser.on_repeat_artist}
                              </p>
                            )}
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="flex items-center justify-center text-blue-700">
                            <Headphones
                              size={12}
                              weight="bold"
                            />
                          </div>

                          <div className="min-w-0">
                            <p className="whitespace-normal break-words text-xs font-semibold leading-tight text-gray-900">
                              {activeUser.on_repeat_title ||
                                "Spotify playlist"}
                            </p>
                          </div>
                        </>
                      )}
                    </a>
                  </div>
                )}
              </div>
            ) : isSelf ? (
              <button
                type="button"
                onClick={startEditingProfile}
                className="
                  mt-3.5 flex flex-col items-center
                  !min-h-0 !min-w-0
                  text-center text-xs
                  group
                "
              >
                <div className="space-y-1.5">
                  <div className="font-semibold text-blue-600">
                    lately
                  </div>

                  <div className="font-semibold text-blue-600">
                    home
                  </div>

                  <div className="font-semibold text-blue-600">
                    on repeat
                  </div>
                </div>

                <p
                  className="
                    mt-2 text-[13px] sm:text-[12px] text-gray-400
                    transition-colors font-medium
                    group-hover:text-blue-500
                  "
                >
                  Share a little more about you!
                </p>
              </button>
            ) : null}
          </>
        )}

        {activeUser.flowers_enabled !== false && (
          <ProfileFlowers
            profileUser={activeUser}
            currentUser={
              isSelf
                ? activeUser
                : meId
                  ? { id: meId }
                  : null
            }
            isSelf={isSelf}
            readOnly={readOnly}
            onOpenWall={
              isCompact
                ? () =>
                    navigate(
                      `/profile/${activeUser.id}/shoutouts`
                    )
                : undefined
            }
          />
        )}

        {/* Stats */}
        <div
          className={`mx-auto grid w-[80%] sm:w-full grid-cols-3 gap-2 ${
            displayBio && !isEditingProfile ? "pt-2" : "pt-2"
          }`}
        >
          {/* Activities */}
          {!readOnly && canSeeLatest ? (
            <button
              type="button"
              onClick={() => {
                if (
                  typeof onActivitiesClick === "function"
                ) {
                  onActivitiesClick();
                } else {
                  goToMyActivities();
                }
              }}
              title="View activities"
              className="
                flex flex-col items-center leading-tight
                rounded-2xl !min-h-0 !min-w-0 px-1 pt-2 sm:py-2
                sm:hover:bg-gray-50
              "
            >
              <span className="font-black text-blue-600">
                {activitiesCount}
              </span>

              <span className="text-[12px] text-gray-500">
                activities
              </span>
            </button>
          ) : (
            <div
              className="
                flex flex-col items-center leading-tight
                rounded-xl !min-h-0 !min-w-0 px-1 pt-2 sm:py-2
                sm:hover:bg-gray-50
              "
            >
              <span className="font-black text-blue-600">
                {activitiesCount}
              </span>

              <span className="text-[12px] text-gray-500">
                activities
              </span>
            </div>
          )}

          {/* Followers */}
          {!readOnly ? (
            <button
              type="button"
              onClick={() => openList("followers")}
              title="View followers"
              className="
                flex flex-col items-center leading-tight
                rounded-xl !min-h-0 !min-w-0 px-1 pt-2 sm:py-2
                sm:hover:bg-gray-50
              "
            >
              <span className="font-black text-blue-600">
                {activeUser.followers ?? 0}
              </span>

              <span className="text-[12px] text-gray-500">
                followers
              </span>
            </button>
          ) : (
            <div
              className="
                flex flex-col items-center leading-tight
                rounded-xl !min-h-0 !min-w-0 px-1 pt-2 sm:py-2
                sm:hover:bg-gray-50
              "
            >
              <span className="font-black text-blue-600">
                {activeUser.followers ?? 0}
              </span>

              <span className="text-[12px] text-gray-500">
                followers
              </span>
            </div>
          )}

          {/* Following */}
          {!readOnly ? (
            <button
              type="button"
              onClick={() => openList("following")}
              title="View following"
              className="
                flex flex-col items-center leading-tight
                rounded-xl !min-h-0 !min-w-0 px-1 pt-2 sm:py-2
                sm:hover:bg-gray-50
              "
            >
              <span className="font-black text-blue-600">
                {activeUser.following ?? 0}
              </span>

              <span className="text-[12px] text-gray-500">
                following
              </span>
            </button>
          ) : (
            <div
              className="
                flex flex-col items-center leading-tight
                rounded-xl !min-h-0 !min-w-0 px-1 pt-2 sm:py-2
                sm:hover:bg-gray-50
              "
            >
              <span className="font-black text-blue-600">
                {activeUser.following ?? 0}
              </span>

              <span className="text-[12px] text-gray-500">
                following
              </span>
            </div>
          )}
        </div>

        {/* Latest: hide completely when there are zero activities */}
        {activitiesCount > 0 && (
          <>
            {showLatestAllowed && latestActivity?.id ? (
              <button
                type="button"
                onClick={handleOpenLatest}
                title="Open latest activity"
                className="
                  sm:mt-0.5 max-w-full
                  text-left text-[13px] text-gray-600
                  hover:text-blue-600
                "
              >
                <span className="font-semibold text-gray-800">
                  Latest:
                </span>{" "}
                <span className="text-blue-600 hover:underline">
                  {latestActivity.title}
                </span>
              </button>
            ) : showLatestLink && !canSeeLatest ? (
              <p className="mt-2 text-sm text-gray-400">
                <span className="font-bold text-gray-600">
                  Latest:
                </span>{" "}
                Follow to see latest
              </p>
            ) : null}
          </>
        )}

        {/* Edit and Share: self only */}
        {isSelf && !isEditingProfile && (
          <div
            className={`flex w-full justify-center gap-2 ${
              activitiesCount > 0 ? "mt-2 sm:mt-4" : "mt-2"
            }`}
          >
            <button
              type="button"
              onClick={startEditingProfile}
              className="
                flex-1 rounded-full bg-gray-100
                px-1 py-1.5 text-xs font-semibold text-gray-700
                !min-h-0 !min-w-0
                hover:bg-blue-200
              "
            >
              Edit
            </button>

            <button
              type="button"
              onClick={shareProfile}
              className="
                flex-1 rounded-full bg-gray-100
                px-1 py-1.5 text-xs font-semibold text-gray-700
                !min-h-0 !min-w-0
                hover:bg-blue-200
              "
            >
              Share
            </button>
          </div>
        )}
        

        {/* Existing connection controls */}
        {!isSelf && (
          <div className="mt-1 sm:mt-3 w-full">
            {connStatus === "none" && (
              <button
                type="button"
                onClick={onConnect}
                className="
                  w-full rounded-full bg-blue-600
                  px-3 py-2 text-sm text-white
                  !min-h-0 !min-w-0
                  hover:bg-blue-700
                "
              >
                Connect
              </button>
            )}

            {connStatus === "pending_out" && (
              <button
                type="button"
                onClick={onRemove}
                className="
                  w-full rounded-full bg-blue-600
                  px-3 py-2 text-sm text-white
                  !min-h-0 !min-w-0
                  hover:bg-blue-700
                "
              >
                Request Sent (Cancel)
              </button>
            )}

            {connStatus === "pending_in" && (
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={onAccept}
                  className="
                    flex-1 rounded-full bg-blue-600
                    px-3 py-2 text-sm text-white
                    !min-h-0 !min-w-0
                    hover:bg-blue-700
                  "
                >
                  Accept
                </button>

                <button
                  type="button"
                  onClick={onRemove}
                  className="
                    flex-1 rounded-full bg-red-400/80
                    px-3 py-2 text-sm text-white
                    !min-h-0 !min-w-0 
                    hover:bg-red-400
                  "
                >
                  Decline
                </button>
              </div>
            )}

            {connStatus === "accepted" && (
              <button
                type="button"
                onClick={onRemove}
                className="
                  w-full rounded-full bg-blue-600
                  px-3 py-2 text-sm text-white
                  !min-h-0 !min-w-0
                  hover:bg-blue-700 
                "
              >
                Connected (Remove)
              </button>
            )}
          </div>
        )}
      </div>

      <CropperModal
        open={showCropper}
        image={cropSrc}
        crop={crop}
        zoom={zoom}
        aspect={1}
        cropPixels={cropPixels}
        setCrop={setCrop}
        setZoom={setZoom}
        onCropComplete={onCropComplete}
        onClose={cancelCrop}
        onSkip={cancelCrop}
        onApply={uploadCropped}
        title="Crop Photo"
        skipLabel="Cancel"
        applyLabel={uploading ? "Uploading…" : "Save"}
        cropShape="round"
        applyDisabled={uploading || !cropPixels}
      />

      {toast &&
        createPortal(
          <CircleToast
            toast={toast}
            onClose={() => setToast(null)}
            onCopyAgain={async () => {
              const ok = await copyText(getProfileUrl());

              if (ok) {
                showToast(
                  "Profile link copied again",
                  "success",
                  2000,
                  true
                );
              } else {
                showToast(
                  "Copy failed — try again",
                  "error"
                );
              }
            }}
            contentClassName="!py-1"
            closeClassName="!top-0.5 sm:!top-3"
          />,
          document.body
        )}

      {/* Internal ConnectionsModal: only render if NOT explicitly disabled */}
      {!disableConnectionsModal && !isCompact && connOpen && createPortal(
        <ConnectionsModal
          key={`conn-${activeUser?.id}-${connTab}`}  // force remount when switching users/tabs
          open
          onClose={() => {
            setConnOpen(false);
            window.dispatchEvent(new CustomEvent("connections:close"));
          }}

          // Whose connections are we viewing?
          userId={activeUser?.id}
          profileUserId={activeUser?.id}
          subjectUserId={activeUser?.id}

          // Who is the viewer (for status/badges and action buttons)?
          meId={meId || null}
          viewerId={meId || null}

          // Which tab
          initialTab={connTab}
        />,
        document.body
      )}

      {lb.open
        ? createPortal(
            <ActivityLightbox
              key={lb.id || 'none'}
              open={lb.open}
              onClose={() => setLb({ open: false, activity: null, id: null })}
              activity={lb.activity}
              activityId={lb.id}
              currentUser={activeUser}
            />,
            document.body
          )
        : null}
    </div>
  );
};

export default ProfileCard;
